import React from 'react';
import { Box, Button, Typography, Collapse } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

class ErrorBoundaryPanel extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            showDetails: false,
            lastPathname: props.location?.pathname || '',
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Error caught in ErrorBoundary:', error, errorInfo);
        this.setState({ error, errorInfo });
    }

    componentDidUpdate(prevProps) {
        const currentPath = this.props.location?.pathname;
        if (currentPath && currentPath !== prevProps.location?.pathname) {
            this.setState({
                hasError: false,
                error: null,
                errorInfo: null,
                showDetails: false,
                lastPathname: currentPath,
            });
        }
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
    };

    toggleDetails = () => {
        this.setState((prevState) => ({ showDetails: !prevState.showDetails }));
    };

    handleGoHome = () => {
        if (this.props.navigate) {
            this.props.navigate('/');
        }
    };

    render() {
        const { hasError, error, errorInfo, showDetails } = this.state;
        const {
            fallback,
            children,
            showTryAgain,
            showGoHome,
        } = this.props;

        if (hasError) {
            return fallback || (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        p: 4,
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h5" gutterBottom>
                        Oops! Something went wrong.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" mb={2}>
                        Please try again or get support.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        {showTryAgain && (
                            <Button variant="contained" onClick={this.handleReset}>
                                Try Again
                            </Button>
                        )}
                        <Button variant="outlined" onClick={this.toggleDetails}>
                            {showDetails ? 'Hide Details' : 'Show Details'}
                        </Button>
                        {showGoHome && (
                            <Button variant="text" onClick={this.handleGoHome}>
                                Go Home
                            </Button>
                        )}
                    </Box>
                    <Collapse in={showDetails}>
                        <Box sx={{
                            backgroundColor: '#f5f5f5',
                            color: '#333',
                            p: 2,
                            borderRadius: 2,
                            textAlign: 'left',
                            maxWidth: '800px',
                            width: '100%',
                            overflowX: 'auto',
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            whiteSpace: 'pre-wrap',
                        }}>
                            {error && <div><strong>Error:</strong> {error.toString()}</div>}
                            {errorInfo && <div style={{ marginTop: '1em' }}>{errorInfo.componentStack}</div>}
                        </Box>
                    </Collapse>
                </Box>
            );
        }

        return children;
    }
}

function ErrorBoundary(props) {
    const navigate = useNavigate();
    const location = useLocation();

    return <ErrorBoundaryPanel {...props} navigate={navigate} location={location} />;
}

export default ErrorBoundary;
